import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { keyframes } from "@emotion/react";
import { Button } from "@mui/material";
import { green } from "@mui/material/colors";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";

/**
 * Botão de exportação assíncrona com progresso — SÓ apresentação. A lógica (enfileirar,
 * polling, download) fica no hook `useExportacaoAssincrona`; este componente recebe o
 * retorno dele em `progresso` e desenha.
 *
 * - Preenchimento em verde claro (green[100]) no fundo do próprio botão, conforme o
 *   percentual. Texto continua escuro sobre as duas metades (contraste >= 4.5:1).
 * - Estado indicado por TEXTO e ÍCONE, não só por cor.
 * - `percentual === null` (servidor não sabe o total) = faixa indeterminada, sem número.
 *   Tipos sem loop no worker chegam como 0/50/100 — mostrado como vier, sem interpolar.
 * - Acessibilidade: progressbar com aria-valuenow/min/max (valuenow omitido se
 *   indeterminado) e região aria-live que anuncia só a MUDANÇA de estado. Durante o job o
 *   botão fica aria-disabled (continua focável pro leitor de tela), não `disabled`.
 * - Largura: medida uma vez (Canvas, com a fonte computada do próprio botão) a partir do
 *   maior rótulo entre todos os estados possíveis, e travada como `minWidth` — o botão nunca
 *   encolhe/cresce ao trocar de "Gerar" pra "Gerando… 45%" pra "Falhou" (2026-10-03).
 *
 * `como="nativo"` renderiza um <button> simples com `className` — para as telas que usam
 * os botões do CSS module (btnHeader/btnExportar) em vez de MUI.
 *
 * `bloquearDuranteJob={false}` — para gatilho de menu (Conciliação SPED, eSocial): o clique
 * abre o menu de variantes, não dispara job, então continua permitido enquanto um roda.
 */

const VERDE_CLARO = green[100]; // #C8E6C9
const COR_TEXTO = "#18316e";

const ESTADOS_ATIVOS = new Set(["enfileirando", "na_fila", "processando", "baixando"]);

const faixaIndeterminada = keyframes`
  from { background-position: 0 0; }
  to { background-position: 28px 0; }
`;

const visualmenteOculto = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

/** Texto visível do botão por estado. */
export function rotuloDoEstado(estado, percentual, rotulo) {
  switch (estado) {
    case "enfileirando":
      return "Solicitando…";
    case "na_fila":
      return "Na fila…";
    case "processando":
      return percentual == null ? "Gerando…" : `Gerando… ${percentual}%`;
    case "baixando":
      return "Baixando…";
    // Igual ao "concluido": o ícone (vermelho/ErrorOutline) já indica o estado, e o detalhe
    // vem por fora do botão (tooltip/`title` com `descricao`, Snackbar via onFinalizar) — um
    // rótulo tipo "Falhou — tentar novamente" virando o PRÓPRIO texto clicável confundia mais
    // do que ajudava (2026-10-03). Um novo clique tenta de novo, como o rótulo normal já diz.
    case "erro":
      return rotulo;
    case "tempo_esgotado":
      return "Ainda gerando — verificar de novo";
    // concluido mantém o nome da ação (o ícone + preenchimento cheio indicam o fim e o
    // anúncio/snackbar dizem "concluída"); um novo clique gera de novo.
    default:
      return rotulo;
  }
}

/** Frase anunciada pela região aria-live — só muda quando o estado muda. */
export function anuncioDoEstado(estado, mensagem) {
  switch (estado) {
    case "enfileirando":
      return "Solicitando exportação.";
    case "na_fila":
      return "Exportação na fila.";
    case "processando":
      return "Gerando arquivo.";
    case "baixando":
      return "Baixando arquivo.";
    case "concluido":
      return "Exportação concluída.";
    case "erro":
      return `Exportação falhou. ${mensagem || ""}`.trim();
    case "tempo_esgotado":
      return mensagem || "A exportação continua em processamento.";
    default:
      return "";
  }
}

function iconeDoEstado(estado) {
  if (estado === "concluido") return <CheckCircleIcon fontSize="small" aria-hidden="true" />;
  if (estado === "erro") return <ErrorOutlineIcon fontSize="small" aria-hidden="true" />;
  if (estado === "tempo_esgotado") return <HourglassEmptyIcon fontSize="small" aria-hidden="true" />;
  return null;
}

/** Fundo do botão: determinado = gradiente até p%; indeterminado = faixa listrada animada. */
export function fundoProgresso(estado, percentual) {
  if (estado === "concluido") {
    return { backgroundImage: `linear-gradient(to right, ${VERDE_CLARO} 100%, transparent 100%)` };
  }
  if (!ESTADOS_ATIVOS.has(estado)) return null;
  if (percentual == null) {
    return {
      backgroundImage: `repeating-linear-gradient(45deg, ${VERDE_CLARO} 0 10px, transparent 10px 20px)`,
      backgroundSize: "28px 28px",
      animation: `${faixaIndeterminada} 1s linear infinite`,
      "@media (prefers-reduced-motion: reduce)": { animation: "none" },
    };
  }
  return {
    backgroundImage: `linear-gradient(to right, ${VERDE_CLARO} ${percentual}%, transparent ${percentual}%)`,
    transition: "background-image 300ms",
    "@media (prefers-reduced-motion: reduce)": { transition: "none" },
  };
}

/** Maior rótulo possível para ESTE botão (dado o `rotulo` ocioso/concluído do chamador),
 * usado só pra medir a largura máxima — nunca é renderizado. 100% é o pior caso de dígitos
 * (processando sempre tem 1 a 3 dígitos). */
function rotulosPossiveis(rotulo) {
  return [
    rotulo,
    "Solicitando…",
    "Na fila…",
    "Gerando… 100%",
    "Baixando…",
    "Ainda gerando — verificar de novo",
  ];
}

/** Mede o texto mais largo com a fonte computada de `elemento` (Canvas 2D — sem layout
 * thrashing, sem renderizar nada escondido no DOM). `null` se a API não existir (SSR/teste). */
function medirMaiorRotuloPx(elemento, rotulos) {
  if (!elemento || typeof document === "undefined" || !document.createElement) return null;
  const canvas = medirMaiorRotuloPx._canvas || (medirMaiorRotuloPx._canvas = document.createElement("canvas"));
  const ctx = canvas.getContext && canvas.getContext("2d");
  if (!ctx) return null;
  const fonte = window.getComputedStyle(elemento).font;
  ctx.font = fonte && fonte !== "" ? fonte : "14px sans-serif";
  let maior = 0;
  for (const texto of rotulos) {
    if (!texto) continue;
    const largura = ctx.measureText(texto).width;
    if (largura > maior) maior = largura;
  }
  return maior;
}

export default function BotaoExportacaoProgresso({
  progresso,
  rotulo,
  onClick,
  disabled = false,
  bloquearDuranteJob = true,
  como = "mui",
  className,
  sx,
  ...rest
}) {
  const idBase = useId();
  const idProgresso = `${idBase}-progresso`;
  const estado = progresso?.estado || "ocioso";
  const percentual = progresso?.percentual ?? null;
  const ativo = ESTADOS_ATIVOS.has(estado);
  const mostrarBarra = ativo || estado === "concluido";
  const valorBarra = estado === "concluido" ? 100 : percentual;
  const texto = rotuloDoEstado(estado, percentual, rotulo);
  const icone = iconeDoEstado(estado);
  const fundo = fundoProgresso(estado, percentual);

  const bloqueado = ativo && bloquearDuranteJob;

  const botaoRef = useRef(null);
  const [minWidth, setMinWidth] = useState(null);

  useLayoutEffect(() => {
    const largura = medirMaiorRotuloPx(botaoRef.current, rotulosPossiveis(rotulo));
    if (largura == null) return;
    // Folga pro ícone (quando aparece) + padding do botão — não precisa ser exata, só não
    // encolher nunca abaixo do pior caso de texto.
    setMinWidth(Math.ceil(largura) + 56);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rotulo]);

  const handleClick = (e) => {
    if (bloqueado || disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  const ariaComum = {
    "aria-busy": ativo || undefined,
    "aria-disabled": bloqueado || undefined,
    "aria-describedby": mostrarBarra ? idProgresso : undefined,
    title: progresso?.descricao || undefined,
  };

  let botao;
  if (como === "nativo") {
    // Base visual vem do className (CSS module da tela); por cima, só a imagem de fundo e a
    // largura mínima medida.
    const estiloNativo = {
      ...(fundo
        ? {
            backgroundImage: fundo.backgroundImage,
            backgroundSize: fundo.backgroundSize,
            color: COR_TEXTO,
          }
        : {}),
      cursor: bloqueado ? "progress" : undefined,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      minWidth: minWidth ?? undefined,
      boxSizing: "border-box",
    };
    botao = (
      <button
        ref={botaoRef}
        type="button"
        className={className}
        disabled={disabled}
        onClick={handleClick}
        style={estiloNativo}
        {...ariaComum}
        {...rest}
      >
        {icone}
        {texto}
      </button>
    );
  } else {
    // Durante o job, o fundo escuro dos botões "contained" vira claro pra o verde aparecer
    // e o texto escuro manter contraste — inclusive no hover.
    const sobreposicao = {
      minWidth: minWidth ?? undefined,
      boxSizing: "border-box",
      ...(fundo
        ? {
            bgcolor: "#fff",
            color: COR_TEXTO,
            borderColor: COR_TEXTO,
            ...fundo,
            "&:hover": { bgcolor: "#fff", borderColor: COR_TEXTO, ...(fundo.backgroundImage ? { backgroundImage: fundo.backgroundImage } : {}) },
          }
        : {}),
      cursor: bloqueado ? "progress" : undefined,
    };
    const sxFinal = [...(Array.isArray(sx) ? sx : [sx]), sobreposicao].filter(Boolean);
    botao = (
      <Button
        ref={botaoRef}
        className={className}
        disabled={disabled}
        onClick={handleClick}
        startIcon={icone}
        sx={sxFinal}
        {...ariaComum}
        {...rest}
      >
        {texto}
      </Button>
    );
  }

  return (
    <span style={{ position: "relative", display: "inline-flex" }}>
      {botao}
      {mostrarBarra && (
        <span
          id={idProgresso}
          role="progressbar"
          aria-label={`Progresso: ${rotulo}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={valorBarra == null ? undefined : valorBarra}
          aria-valuetext={progresso?.descricao || texto}
          style={visualmenteOculto}
        />
      )}
      <span aria-live="polite" role="status" style={visualmenteOculto}>
        {anuncioDoEstado(estado, progresso?.mensagem)}
      </span>
    </span>
  );
}
